import { useMemo, useState } from 'react'
import { api } from '../api'
import { CloudMark, vendorLabel } from '../components/CloudMark'
import { Icon } from '../components/Icon'
import { Badge, Card, Empty, Modal, Progress, formatDate, formatQuota, formatQuotaValue, formatRemaining, serviceLabel } from '../components/UI'
import { providerExpired, providerPresets, quotaPercent, remainingPercent, presetForKind, toDatetimeLocal, toISOStringOrEmpty, vendorForProvider, vendorForType, type CloudVendor, type ProviderPreset } from '../lib/providers'
import type { AppData, Provider, ProviderType } from '../types'

interface ProviderForm {
  name: string
  kind: string
  endpoint: string
  region: string
  priority: string
  enabled: boolean
  secret: string
  analyticsToken: string
  limit: string
  used: string
  unit: string
  warning: string
  hard: string
  path: string
  bucket: string
  publicDomain: string
  zoneId: string
  usageMode: 'manual' | 'http' | 'reported'
  usageUrl: string
  usedPath: string
  limitPath: string
  authHeader: string
  authPrefix: string
  gatewayEnabled: boolean
  accountId: string
  projectId: string
  subscriptionId: string
  resourceGroup: string
  metric: string
  resetPeriod: 'daily' | 'monthly' | 'yearly' | 'none'
  resetAt: string
  expiresAt: string
  cycleLabel: string
  readOnly: boolean
  note: string
}

const emptyForm: ProviderForm = {
  name: '', kind: 'aws_s3', endpoint: '', region: '', priority: '100', enabled: true, secret: '', analyticsToken: '',
  limit: '0', used: '0', unit: 'requests', warning: '80', hard: '95', path: '', bucket: '', publicDomain: '', zoneId: '',
  usageMode: 'manual', usageUrl: '', usedPath: 'used', limitPath: 'limit', authHeader: 'Authorization', authPrefix: 'Bearer', gatewayEnabled: false,
  accountId: '', projectId: '', subscriptionId: '', resourceGroup: '', metric: '', resetPeriod: 'monthly', resetAt: '', expiresAt: '', cycleLabel: '', readOnly: false, note: '',
}

const vendorFilters: Array<'all' | CloudVendor> = ['all', 'aws', 'azure', 'google', 'cloudflare', 'oracle', 'alibaba', 'local', 'other']

export function ProvidersPage({ data, reload, notify }: { data: AppData; reload: () => Promise<void>; notify: (message: string, tone?: 'success' | 'error') => void }) {
  const [vendorFilter, setVendorFilter] = useState<'all' | CloudVendor>('all')
  const [serviceFilter, setServiceFilter] = useState('all')
  const [modal, setModal] = useState<'create' | 'edit' | 'usage' | null>(null)
  const [selected, setSelected] = useState<Provider | null>(null)
  const [form, setForm] = useState<ProviderForm>(emptyForm)
  const [busy, setBusy] = useState('')
  const services = ['all', ...Array.from(new Set(data.providers.map((provider) => provider.service)))]
  const filtered = useMemo(() => data.providers.filter((provider) => {
    const vendorMatch = vendorFilter === 'all' || vendorForProvider(provider) === vendorFilter
    const serviceMatch = serviceFilter === 'all' || provider.service === serviceFilter
    return vendorMatch && serviceMatch
  }), [data.providers, serviceFilter, vendorFilter])
  const type = data.providerTypes.find((item) => item.kind === form.kind) || data.providerTypes[0]
  const cloudTypes = data.providerTypes.filter((item) => !['dns'].includes(item.service))

  const openCreate = (kind?: string) => {
    const requested = kind || data.providerTypes[0]?.kind || 'aws_s3'
    setSelected(null)
    setForm(formForKind(requested, data.providerTypes.find((item) => item.kind === requested)))
    setModal('create')
  }

  const openEdit = (provider: Provider) => {
    setSelected(provider)
    setForm(toForm(provider))
    setModal('edit')
  }

  const openUsage = (provider: Provider) => {
    setSelected(provider)
    setForm(toForm(provider))
    setModal('usage')
  }

  const changeKind = (kind: string) => {
    const next = formForKind(kind, data.providerTypes.find((item) => item.kind === kind))
    setForm({ ...next, enabled: form.enabled })
  }

  const saveProvider = async () => {
    if (!form.name.trim()) return notify('请填写资源名称', 'error')
    if (form.kind === 'google_drive' && !form.path.trim()) return notify('请填写 Google Drive 的 Rclone 挂载路径', 'error')
    const providerType = data.providerTypes.find((item) => item.kind === form.kind)
    if (!providerType) return notify('请选择资源类型', 'error')
    const warning = Number(form.warning)
    const hard = Number(form.hard)
    if (!Number.isFinite(warning) || warning < 0 || warning > 100) return notify('预警线必须是 0 到 100 之间的数字，例如 80', 'error')
    if (!Number.isFinite(hard) || hard < 0 || hard > 100) return notify('硬停线必须是 0 到 100 之间的数字，例如 95', 'error')
    if (hard > 0 && warning > hard) return notify('预警线不能高于硬停线，推荐填写 80 / 95', 'error')
    setBusy('save')
    const config: Record<string, string> = {
      vendor: String(vendorForType(providerType)),
      usage_mode: form.usageMode,
      reset_period: form.resetPeriod,
    }
    setConfig(config, 'path', form.path)
    setConfig(config, 'bucket', form.bucket)
    setConfig(config, 'container', form.bucket)
    setConfig(config, 'public_domain', form.publicDomain)
    setConfig(config, 'zone_id', form.zoneId)
    setConfig(config, 'usage_url', form.usageUrl)
    setConfig(config, 'used_path', form.usedPath)
    setConfig(config, 'limit_path', form.limitPath)
    setConfig(config, 'auth_header', form.authHeader)
    setConfig(config, 'auth_prefix', form.authPrefix)
    setConfig(config, 'account_id', form.accountId)
    setConfig(config, 'project_id', form.projectId)
    setConfig(config, 'subscription_id', form.subscriptionId)
    setConfig(config, 'resource_group', form.resourceGroup)
    setConfig(config, 'metric', form.metric)
    setConfig(config, 'reset_at', toISOStringOrEmpty(form.resetAt))
    setConfig(config, 'expires_at', toISOStringOrEmpty(form.expiresAt))
    setConfig(config, 'cycle_label', form.cycleLabel)
    setConfig(config, 'read_only', form.readOnly ? 'true' : '')
    setConfig(config, 'note', form.note)
    if (form.gatewayEnabled) config.gateway_enabled = 'true'
    const payload = {
      name: form.name.trim(), kind: form.kind, category: providerType.category, service: providerType.service,
      endpoint: form.endpoint.trim(), region: form.region.trim(), enabled: form.enabled, priority: number(form.priority, 100),
      status: selected?.status === 'expired' ? 'unknown' : selected?.status || 'unknown', config, secret: form.secret.trim(), analytics_secret: form.analyticsToken.trim(),
      quota: { limit: number(form.limit), used: number(form.used), unit: form.unit.trim() || 'requests', warning_percent: warning, hard_stop_percent: hard },
    }
    try {
      if (modal === 'edit' && selected) await api.updateProvider(selected.id, payload)
      else await api.createProvider(payload)
      notify(modal === 'edit' ? '资源配置已更新' : '资源已加入自动调度池', 'success')
      setModal(null)
      await reload()
    } catch (error) {
      notify(error instanceof Error ? error.message : '保存资源失败', 'error')
    } finally { setBusy('') }
  }

  const saveUsage = async () => {
    if (!selected) return
    setBusy('usage')
    try {
      await api.setProviderUsage(selected.id, number(form.used), number(form.limit), form.unit.trim())
      notify('用量与剩余额度已更新，系统已重新选路', 'success')
      setModal(null)
      await reload()
    } catch (error) {
      notify(error instanceof Error ? error.message : '更新用量失败', 'error')
    } finally { setBusy('') }
  }

  const refresh = async (provider: Provider) => {
    setBusy(`refresh:${provider.id}`)
    try {
      await api.refreshProvider(provider.id)
      notify(`${provider.name} 用量同步完成`, 'success')
    } catch (error) {
      notify(error instanceof Error ? error.message : '同步失败', 'error')
    } finally {
      setBusy('')
      await reload()
    }
  }

  const refreshAll = async () => {
    const targets = filtered.filter((provider) => provider.enabled)
    if (!targets.length) return
    setBusy('refresh-all')
    let failures = 0
    for (const provider of targets) {
      try { await api.refreshProvider(provider.id) } catch { failures++ }
    }
    await reload()
    setBusy('')
    notify(failures ? `同步完成，${failures} 项需要检查配置` : `已同步 ${targets.length} 项资源`, failures ? 'error' : 'success')
  }

  const toggle = async (provider: Provider) => {
    setBusy(`toggle:${provider.id}`)
    try {
      await api.updateProvider(provider.id, fullPayload(provider, { enabled: !provider.enabled }))
      notify(`${provider.name} 已${provider.enabled ? '暂停调度' : '重新启用'}`, 'success')
      await reload()
    } catch (error) {
      notify(error instanceof Error ? error.message : '状态更新失败', 'error')
    } finally { setBusy('') }
  }

  const remove = async (provider: Provider) => {
    if (!window.confirm(`确定删除“${provider.name}”吗？路由中的引用也会被移除。`)) return
    setBusy(`delete:${provider.id}`)
    try {
      await api.deleteProvider(provider.id)
      notify('资源已删除', 'success')
      await reload()
    } catch (error) {
      notify(error instanceof Error ? error.message : '删除失败', 'error')
    } finally { setBusy('') }
  }

  return <div className="page-stack apple-resources-page">
    <section className="resource-page-hero">
      <div><span className="apple-eyebrow">CLOUD CONNECTIONS</span><h2>把赠送额度变成可自动切换的资源。</h2><p>每个 API、试用金或免费层都是独立的配额池。TianCloud 记录已用量、剩余量、重置周期和到期时间，并在资源不可用前停止继续分发。</p></div>
      <button className="button button-primary" onClick={() => openCreate()}><Icon name="plus" />接入新资源</button>
    </section>

    <section className="provider-template-strip">
      {providerPresets.filter((preset) => ['cloudflare_r2', 'aws_polly', 'azure_ai_search', 'google_vertex_ai', 'google_drive', 'oracle_compute', 'alibaba_ecs', 'alibaba_model_studio', 'ollama'].includes(preset.kind)).map((preset) => <button className={`provider-template template-${preset.vendor}`} key={preset.kind} onClick={() => openCreate(preset.kind)}>
        <CloudMark vendor={preset.vendor} />
        <div><strong>{preset.name}</strong><span>{preset.description}</span></div>
        <Icon name="plus" />
      </button>)}
    </section>

    <section className="cloud-account-summary">
      {(['aws', 'azure', 'google', 'cloudflare', 'oracle', 'alibaba', 'local'] as CloudVendor[]).map((vendor) => {
        const items = data.providers.filter((provider) => vendorForProvider(provider) === vendor)
        const active = items.filter((provider) => provider.enabled && !['error', 'quota_exhausted', 'expired', 'needs_config'].includes(provider.status)).length
        return <button key={vendor} className={`account-summary account-${vendor} ${vendorFilter === vendor ? 'selected' : ''}`} onClick={() => setVendorFilter(vendorFilter === vendor ? 'all' : vendor)}>
          <CloudMark vendor={vendor} size="lg" /><div><small>{vendorLabel(vendor)}</small><strong>{items.length}</strong><span>{active} 项可调度</span></div><Icon name="chevron" />
        </button>
      })}
    </section>

    <section className="resource-toolbar-apple">
      <div className="resource-filter-stack">
        <div className="segmented vendor-segmented">{vendorFilters.map((vendor) => <button key={vendor} className={vendorFilter === vendor ? 'active' : ''} onClick={() => setVendorFilter(vendor)}>{vendor === 'all' ? '全部平台' : vendorLabel(vendor)}</button>)}</div>
        <div className="service-filter-pills">{services.map((service) => <button key={service} className={serviceFilter === service ? 'active' : ''} onClick={() => setServiceFilter(service)}>{service === 'all' ? '全部服务' : serviceLabel(service)}<span>{service === 'all' ? data.providers.length : data.providers.filter((provider) => provider.service === service).length}</span></button>)}</div>
      </div>
      <button className="button button-secondary" disabled={busy === 'refresh-all'} onClick={refreshAll}><Icon name="refresh" className={busy === 'refresh-all' ? 'spin' : ''} />同步当前列表</button>
    </section>

    {filtered.length === 0 ? <Card><Empty icon="cloud" title="这里还没有资源" description="从上方模板选择 Cloudflare、AWS、Azure、Google Cloud、Oracle、Alibaba Cloud International 或本地服务，几步即可加入自动调度。" /></Card> : <section className="provider-card-grid-apple">{filtered.map((provider) => {
      const percent = quotaPercent(provider)
      const expired = providerExpired(provider)
      const resetAt = provider.config?.reset_at
      const expiresAt = provider.config?.expires_at
      return <article className={`provider-card-apple provider-${vendorForProvider(provider)} ${!provider.enabled ? 'disabled' : ''} ${expired ? 'expired' : ''}`} key={provider.id}>
        <div className="provider-card-accent" />
        <div className="provider-card-top-apple"><CloudMark vendor={vendorForProvider(provider)} size="lg" /><div><span>{vendorLabel(vendorForProvider(provider))} · {serviceLabel(provider.service)}</span><strong>{provider.name}</strong><small>{provider.region || provider.config?.project_id || provider.config?.subscription_id || provider.kind}</small></div><button className="icon-button" onClick={() => openEdit(provider)} aria-label="编辑资源"><Icon name="settings" /></button></div>
        <div className="provider-card-status"><Badge status={!provider.enabled ? 'disabled' : expired ? 'expired' : provider.status} /><span>优先级 {provider.priority}</span><span>{provider.has_secret ? <><Icon name="lock" size={13} />凭据已加密</> : '未保存凭据'}</span></div>
        {provider.config?.read_only === 'true' && <div className="provider-readonly-note"><Badge status="warning">只读</Badge>{provider.config?.note && <span>{provider.config.note}</span>}</div>}
        {provider.config?.read_only !== 'true' && provider.config?.note && <p className="provider-note">{provider.config.note}</p>}
        <div className="provider-remaining-block"><div><small>本周期剩余</small><strong>{formatRemaining(provider.quota)}</strong><span>{provider.quota.limit > 0 ? `总额 ${formatQuotaValue(provider.quota.limit, provider.quota.unit)}` : '不设额度上限'}</span></div><div className="remaining-ring" style={{ '--remaining': `${remainingPercent(provider) * 3.6}deg` } as React.CSSProperties}><span>{provider.quota.limit > 0 ? `${remainingPercent(provider).toFixed(0)}%` : '∞'}</span></div></div>
        <Progress value={percent} warning={provider.quota.warning_percent} hard={provider.quota.hard_stop_percent} />
        <div className="provider-cycle-row"><span><Icon name="activity" />已用 {formatQuota(provider.quota).split(' / ')[0]}</span><span><Icon name="clock" />{expiresAt ? `到期 ${formatDate(expiresAt, false)}` : resetAt ? `重置 ${formatDate(resetAt, false)}` : cycleName(provider.config?.reset_period)}</span></div>
        {provider.kind === 'cloudflare_r2' && provider.config?.analytics_configured === 'true' && <div className="r2-analytics-row">
          <span><small>Class A 写入</small><strong>{formatRequestCount(provider.config?.r2_class_a_requests)}</strong></span>
          <span><small>Class B 读取</small><strong>{formatRequestCount(provider.config?.r2_class_b_requests)}</strong></span>
          <span><small>R2 存储</small><strong>{formatByteCount(provider.config?.r2_storage_bytes)}</strong></span>
        </div>}
        <div className="provider-card-actions-apple"><button className="button button-small button-secondary" onClick={() => refresh(provider)} disabled={busy === `refresh:${provider.id}`}><Icon name="refresh" className={busy === `refresh:${provider.id}` ? 'spin' : ''} />同步</button><button className="button button-small button-secondary" onClick={() => openUsage(provider)}><Icon name="edit" />用量</button><button className="button button-small button-ghost" onClick={() => toggle(provider)} disabled={busy === `toggle:${provider.id}`}>{provider.enabled ? '暂停' : '启用'}</button><button className="icon-button danger" onClick={() => remove(provider)} disabled={busy === `delete:${provider.id}`}><Icon name="trash" /></button></div>
      </article>
    })}</section>}

    <Modal open={modal === 'create' || modal === 'edit'} title={modal === 'edit' ? '编辑云资源' : '接入云资源'} onClose={() => setModal(null)} wide>
      <div className="provider-connect-heading"><CloudMark vendor={vendorForType(type)} size="lg" /><div><span>{type?.vendor ? vendorLabel(vendorForType(type)) : 'RESOURCE PROVIDER'}</span><strong>{type?.label || '资源提供方'}</strong><p>{type?.description || presetForKind(form.kind)?.description || '配置 API、额度与自动分流策略。'}</p></div><Badge status={form.enabled ? 'healthy' : 'disabled'}>{form.enabled ? '加入调度' : '暂不启用'}</Badge></div>
      <div className="form-section-title"><span>01</span><div><strong>资源身份</strong><small>选择服务类型并填写云账户中的资源标识</small></div></div>
      <div className="form-grid provider-form apple-form-grid">
        <label><span>资源类型</span><select value={form.kind} onChange={(event) => changeKind(event.target.value)} disabled={modal === 'edit'}>{cloudTypes.map((item) => <option value={item.kind} key={item.kind}>{item.label}</option>)}</select></label>
        <label><span>显示名称</span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="例如 Google AI 试用金" /></label>
        <label className="span-2"><span>API / 服务端点</span><input value={form.endpoint} onChange={(event) => setForm({ ...form, endpoint: event.target.value })} placeholder={type?.suggested_endpoint || 'https://... 或 http://127.0.0.1:11434'} /></label>
        <label><span>区域</span><input value={form.region} onChange={(event) => setForm({ ...form, region: event.target.value })} placeholder="eu-west-2 / uksouth" /></label>
        <label><span>路由优先级</span><input type="number" min="0" value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })} /><small>数字越小越优先，本地保底建议 1000。</small></label>
        {vendorForType(type) === 'aws' && <label className="span-2"><span>AWS Account ID / 资源标识</span><input value={form.accountId} onChange={(event) => setForm({ ...form, accountId: event.target.value })} placeholder="可选，用于区分多个账户" /></label>}
        {form.kind === 'cloudflare_r2' && <label className="span-2"><span>Cloudflare Account ID</span><input value={form.accountId} onChange={(event) => {
          const accountId = event.target.value.trim()
          const previousEndpoint = form.accountId.trim() ? `https://${form.accountId.trim()}.r2.cloudflarestorage.com` : ''
          const endpoint = !form.endpoint.trim() || form.endpoint.trim() === previousEndpoint
            ? (accountId ? `https://${accountId}.r2.cloudflarestorage.com` : '')
            : form.endpoint
          setForm({ ...form, accountId, endpoint })
        }} placeholder="Cloudflare 控制台中的 Account ID；填写后自动生成 Endpoint" /></label>}
        {vendorForType(type) === 'azure' && <><label><span>Subscription ID</span><input value={form.subscriptionId} onChange={(event) => setForm({ ...form, subscriptionId: event.target.value })} /></label><label><span>Resource Group</span><input value={form.resourceGroup} onChange={(event) => setForm({ ...form, resourceGroup: event.target.value })} /></label></>}
        {vendorForType(type) === 'google' && <label className="span-2"><span>Google Cloud Project ID</span><input value={form.projectId} onChange={(event) => setForm({ ...form, projectId: event.target.value })} placeholder="my-project-123" /></label>}
        {vendorForType(type) === 'oracle' && <label className="span-2"><span>OCI Tenancy / Compartment 标识</span><input value={form.accountId} onChange={(event) => setForm({ ...form, accountId: event.target.value })} placeholder="可选，用于区分 Tenancy 或 Compartment" /></label>}
        {vendorForType(type) === 'alibaba' && <label className="span-2"><span>Alibaba Cloud International Account / Resource 标识</span><input value={form.accountId} onChange={(event) => setForm({ ...form, accountId: event.target.value })} placeholder="可选，用于区分国际站账户或资源组" /></label>}
      </div>

      <div className="form-section-title"><span>02</span><div><strong>额度与生命周期</strong><small>显示已用和剩余量，并在重置或到期时自动调整路由</small></div></div>
      <div className="form-grid apple-form-grid">
        <label><span>已使用</span><input type="number" min="0" step="any" value={form.used} onChange={(event) => setForm({ ...form, used: event.target.value })} /></label>
        <label><span>总额度</span><input type="number" min="0" step="any" value={form.limit} onChange={(event) => setForm({ ...form, limit: event.target.value })} /></label>
        <label><span>单位</span><input value={form.unit} onChange={(event) => setForm({ ...form, unit: event.target.value })} placeholder="chars / requests / minutes / USD" /></label>
        <label><span>额度周期</span><select value={form.resetPeriod} onChange={(event) => {
          const resetPeriod = event.target.value as ProviderForm['resetPeriod']
          setForm({ ...form, resetPeriod, resetAt: resetPeriod === 'none' ? '' : form.resetAt || defaultResetAt(resetPeriod) })
        }}><option value="daily">每日重置</option><option value="monthly">每月重置</option><option value="yearly">每年重置</option><option value="none">试用期 / 不自动重置</option></select></label>
        <label><span>下一次重置</span><input type="datetime-local" value={form.resetAt} onChange={(event) => setForm({ ...form, resetAt: event.target.value })} disabled={form.resetPeriod === 'none'} /></label>
        <label><span>试用到期</span><input type="datetime-local" value={form.expiresAt} onChange={(event) => setForm({ ...form, expiresAt: event.target.value })} /><small>到期后自动从路由中跳过。</small></label>
        <label><span>预警线 %</span><input type="number" min="0" max="100" value={form.warning} aria-invalid={Number(form.warning) < 0 || Number(form.warning) > 100} onChange={(event) => setForm({ ...form, warning: event.target.value })} /><small>0–100，推荐 80。</small></label>
        <label><span>硬停线 %</span><input type="number" min="0" max="100" value={form.hard} aria-invalid={Number(form.hard) < 0 || Number(form.hard) > 100} onChange={(event) => setForm({ ...form, hard: event.target.value })} /><small>0–100，推荐 95。</small></label>
        <label className="span-2"><span>周期备注</span><input value={form.cycleLabel} onChange={(event) => setForm({ ...form, cycleLabel: event.target.value })} placeholder="例如：新账户试用、每月免费层、实验室预算" /></label>
        <label className="span-2"><span>备注</span><input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} placeholder="例如：订阅已到期，仅用于读取景区位置信息" /></label>
      </div>

      <div className="form-section-title"><span>03</span><div><strong>用量同步与密钥</strong><small>手动维护、由应用主动回报，或从 JSON API 自动拉取</small></div></div>
      <div className="form-grid apple-form-grid">
        <label><span>用量来源</span><select value={form.usageMode} onChange={(event) => setForm({ ...form, usageMode: event.target.value as ProviderForm['usageMode'] })}><option value="reported">应用调用后主动回报</option><option value="http">从 JSON 用量 API 拉取</option><option value="manual">手动维护</option></select></label>
        <label><span>额度指标名</span><input value={form.metric} onChange={(event) => setForm({ ...form, metric: event.target.value })} placeholder="search_requests / tts_characters" /></label>
        {form.usageMode === 'http' && <><label className="span-2"><span>JSON 用量查询 URL</span><input value={form.usageUrl} onChange={(event) => setForm({ ...form, usageUrl: event.target.value })} placeholder="https://api.example.com/v1/usage" /></label><label><span>已用量字段路径</span><input value={form.usedPath} onChange={(event) => setForm({ ...form, usedPath: event.target.value })} placeholder="usage.used" /></label><label><span>总额度字段路径</span><input value={form.limitPath} onChange={(event) => setForm({ ...form, limitPath: event.target.value })} placeholder="usage.limit" /></label></>}
        {form.kind !== 'cloudflare_r2' && <><label><span>鉴权 Header</span><input value={form.authHeader} onChange={(event) => setForm({ ...form, authHeader: event.target.value })} /></label><label><span>鉴权前缀</span><input value={form.authPrefix} onChange={(event) => setForm({ ...form, authPrefix: event.target.value })} placeholder="Bearer；无需前缀可留空" /></label></>}
        <label className="span-2"><span>{type?.secret_label || 'API 密钥 / 凭据 JSON'} {selected?.has_secret && '（留空保持原值）'}</span><textarea rows={4} value={form.secret} onChange={(event) => setForm({ ...form, secret: event.target.value })} placeholder={type?.credential_hint || 'API Key，或只包含必要字段的凭据 JSON。内容由服务端加密保存。'} /></label>
        {form.kind === 'cloudflare_r2' && <label className="span-2"><span>Account Analytics 只读令牌 {selected?.config?.analytics_configured === 'true' && '（已配置，留空保持原值）'}</span><input type="password" autoComplete="new-password" value={form.analyticsToken} onChange={(event) => setForm({ ...form, analyticsToken: event.target.value })} placeholder="Cloudflare API Token：Account Analytics · Read" /><small>仅用于读取 R2 本月操作数和存储量；令牌由服务端加密保存，保存后不会回显。</small></label>}
        {(form.kind === 'local_mount' || form.kind.includes('drive')) && <label className="span-2"><span>挂载路径</span><input value={form.path} onChange={(event) => setForm({ ...form, path: event.target.value })} placeholder="/map" />{form.kind === 'google_drive' && <small>填写控制面容器内可见的 Rclone 挂载路径。</small>}</label>}
        {form.kind.includes('drive') && <><label className="checkbox-field span-2"><input type="checkbox" checked={form.readOnly} onChange={(event) => setForm({ ...form, readOnly: event.target.checked })} /><span>标记为只读资源</span></label><small className="span-2 provider-readonly-help">此标记会显示在资源卡片上；它不会更改服务器上的 Rclone 挂载权限。</small></>}
        {['oracle_oss', 'alibaba_oss', 'azure_blob', 'aws_s3', 'cloudflare_r2'].includes(form.kind) && <label className="span-2"><span>{form.kind === 'cloudflare_r2' ? 'R2 Bucket' : 'Bucket / Container'}</span><input value={form.bucket} onChange={(event) => setForm({ ...form, bucket: event.target.value })} placeholder={form.kind === 'cloudflare_r2' ? 'tianoi-public-assets' : ''} /></label>}
        {form.kind === 'cloudflare_r2' && <><label className="span-2"><span>公开访问域名（可选）</span><input value={form.publicDomain} onChange={(event) => setForm({ ...form, publicDomain: event.target.value })} placeholder="https://assets.tianoi.com" /></label><div className="guardrail-note compact span-2"><Icon name="cloud" /><div><strong>R2 使用 S3 SigV4</strong><p>面板负责加密保存凭据、能力发现与配额路由；上传下载由 rclone 或 S3 SDK 完成，统一 Gateway 保持关闭。</p></div></div></>}
        {form.kind === 'cloudflare' && <label className="span-2"><span>Cloudflare Zone ID</span><input value={form.zoneId} onChange={(event) => setForm({ ...form, zoneId: event.target.value })} /></label>}
        {form.kind !== 'cloudflare_r2' && <label className="checkbox-field"><input type="checkbox" checked={form.gatewayEnabled} onChange={(event) => setForm({ ...form, gatewayEnabled: event.target.checked })} /><span>允许统一 Gateway 转发请求</span></label>}
        <label className="checkbox-field"><input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} /><span>保存后立即参与自动调度</span></label>
      </div>
      <div className="guardrail-note compact"><Icon name="lock" /><div><strong>密钥不会返回到浏览器</strong><p>服务端使用 AES-256-GCM 保存；路由只读取资源状态和额度，不向其他应用暴露管理凭据。</p></div></div>
      <div className="modal-actions"><button className="button button-ghost" onClick={() => setModal(null)}>取消</button><button className="button button-primary" onClick={saveProvider} disabled={busy === 'save'}>{busy === 'save' && <Icon name="refresh" className="spin" />}保存并加入资源池</button></div>
    </Modal>

    <Modal open={modal === 'usage'} title={`更新用量 · ${selected?.name || ''}`} onClose={() => setModal(null)}>
      <div className="usage-edit-summary"><CloudMark vendor={selected ? vendorForProvider(selected) : 'other'} size="lg" /><div><small>当前记录</small><strong>{selected ? formatQuota(selected.quota) : '—'}</strong><span>保存后会立即重新计算路由资格</span></div></div>
      <div className="form-grid"><label><span>已使用</span><input type="number" min="0" step="any" value={form.used} onChange={(event) => setForm({ ...form, used: event.target.value })} /></label><label><span>总额度</span><input type="number" min="0" step="any" value={form.limit} onChange={(event) => setForm({ ...form, limit: event.target.value })} /></label><label className="span-2"><span>单位</span><input value={form.unit} onChange={(event) => setForm({ ...form, unit: event.target.value })} /></label></div>
      <div className="guardrail-note compact warning"><Icon name="alert" /><div><strong>达到硬停线后自动换源</strong><p>资源会保留在控制台中，但不会继续接收新请求，直到额度重置或手动修改。</p></div></div>
      <div className="modal-actions"><button className="button button-ghost" onClick={() => setModal(null)}>取消</button><button className="button button-primary" onClick={saveUsage} disabled={busy === 'usage'}>更新并重新选路</button></div>
    </Modal>
  </div>
}

function formForKind(kind: string, type?: ProviderType): ProviderForm {
  const preset = presetForKind(kind)
  return {
    ...emptyForm,
    kind,
    name: preset?.name || type?.label || '',
    endpoint: preset?.endpoint || type?.suggested_endpoint || '',
    region: preset?.region || '',
    priority: String(preset?.priority ?? 100),
    unit: preset?.unit || type?.default_unit || 'requests',
    usageMode: preset?.usageMode || 'manual',
    authHeader: preset?.authHeader ?? type?.default_auth_header ?? 'Authorization',
    authPrefix: preset?.authPrefix ?? type?.default_auth_prefix ?? 'Bearer',
    gatewayEnabled: preset?.gatewayEnabled ?? type?.gateway_recommended ?? false,
    resetPeriod: preset?.resetPeriod || 'monthly',
    resetAt: defaultResetAt(preset?.resetPeriod || 'monthly'),
    metric: preset?.metric || '',
    path: kind === 'google_drive' ? '/map' : '',
    readOnly: kind === 'google_drive',
    note: kind === 'google_drive' ? '订阅已到期，仅用于读取景区位置信息' : '',
  }
}

function toForm(provider: Provider): ProviderForm {
  const preset = presetForKind(provider.kind)
  return {
    ...emptyForm, name: provider.name, kind: provider.kind, endpoint: provider.endpoint || '', region: provider.region || '', priority: String(provider.priority), enabled: provider.enabled,
    limit: String(provider.quota.limit), used: String(provider.quota.used), unit: provider.quota.unit, warning: String(provider.quota.warning_percent), hard: String(provider.quota.hard_stop_percent),
    path: provider.config?.path || '', bucket: provider.config?.bucket || provider.config?.container || '', publicDomain: provider.config?.public_domain || '', zoneId: provider.config?.zone_id || '', usageMode: (provider.config?.usage_mode as ProviderForm['usageMode']) || preset?.usageMode || 'manual', usageUrl: provider.config?.usage_url || '',
    usedPath: provider.config?.used_path || 'used', limitPath: provider.config?.limit_path || 'limit', authHeader: provider.config?.auth_header ?? preset?.authHeader ?? 'Authorization', authPrefix: provider.config?.auth_prefix ?? preset?.authPrefix ?? 'Bearer', gatewayEnabled: provider.config?.gateway_enabled === 'true', secret: '', analyticsToken: '',
    accountId: provider.config?.account_id || '', projectId: provider.config?.project_id || '', subscriptionId: provider.config?.subscription_id || '', resourceGroup: provider.config?.resource_group || '', metric: provider.config?.metric || preset?.metric || '',
    resetPeriod: (provider.config?.reset_period as ProviderForm['resetPeriod']) || preset?.resetPeriod || 'none', resetAt: toDatetimeLocal(provider.config?.reset_at), expiresAt: toDatetimeLocal(provider.config?.expires_at), cycleLabel: provider.config?.cycle_label || '',
    readOnly: provider.config?.read_only === 'true', note: provider.config?.note || '',
  }
}

function fullPayload(provider: Provider, overrides: Record<string, unknown> = {}) {
  return {
    name: provider.name, kind: provider.kind, category: provider.category, service: provider.service, endpoint: provider.endpoint || '', region: provider.region || '', enabled: provider.enabled,
    priority: provider.priority, status: provider.status, quota: provider.quota, config: provider.config || {}, ...overrides,
  }
}

function setConfig(config: Record<string, string>, key: string, value: string) {
  if (value.trim()) config[key] = value.trim()
}

function number(value: string, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function formatRequestCount(value?: string): string {
  const count = Number(value || 0)
  if (!Number.isFinite(count)) return '—'
  return new Intl.NumberFormat('zh-CN', { notation: count >= 10_000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(count)
}

function formatByteCount(value?: string): string {
  const bytes = Number(value || 0)
  if (!Number.isFinite(bytes)) return '—'
  if (bytes < 1024) return `${bytes.toFixed(0)} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let amount = bytes / 1024
  let unit = units[0]
  for (let index = 1; index < units.length && amount >= 1024; index++) {
    amount /= 1024
    unit = units[index]
  }
  return `${amount.toFixed(amount >= 10 ? 1 : 2)} ${unit}`
}

function cycleName(period?: string): string {
  const names: Record<string, string> = { daily: '每日重置', monthly: '每月重置', yearly: '每年重置', none: '不自动重置' }
  return names[period || ''] || '未设置周期'
}

function defaultResetAt(period: ProviderForm['resetPeriod']): string {
  if (period === 'none') return ''
  const now = new Date()
  let next: Date
  if (period === 'daily') next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
  else if (period === 'monthly') next = new Date(now.getFullYear(), now.getMonth() + 1, 1)
  else next = new Date(now.getFullYear() + 1, 0, 1)
  const offset = next.getTimezoneOffset() * 60_000
  return new Date(next.getTime() - offset).toISOString().slice(0, 16)
}
